import React, { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { Alert, Button, Modal } from 'antd';
import { useIntl } from 'react-intl';
import type { FilerobotImageEditorConfig } from 'react-filerobot-image-editor';
import {
  clearEditingClaim,
  closeModal,
  getIsVisibleModal,
  selectEditingClaim,
} from '../../../redux/store/ui/common';
import { submitAnnotatedClaimImage } from '../../../redux/store/userData/claims';
import {
  selectBrands,
  selectFleets,
} from '../../../redux/store/appData/fleets';
import { fetchImageBlob } from '../../../utils/fetchImageBlob';
import LoadingIcon from '../../shared/LoadingIcon';
import { colors, zIndex } from '../../../theme/theme';
import styles from './ClaimImageAnnotateModal.styles';

const FilerobotImageEditor = lazy(() => import('react-filerobot-image-editor'));

const MODAL_NAME = 'ClaimImageAnnotateModal';
const FALLBACK_MARK_COLOR = colors.statusAlert;

const asHexColor = (value?: string) => {
  const hex = (value || '').trim().replace(/^#/, '');
  return /^([0-9a-f]{3}|[0-9a-f]{6})$/i.test(hex) ? `#${hex}` : undefined;
};
const SAVED_IMAGE = { name: 'damage', extension: 'jpeg', quality: 0.9 };

// Filerobot's purple accent swapped for the antd primary blue.
const EDITOR_PALETTE = {
  'accent-primary': colors.editorAccent,
  'accent-primary-hover': '#4096FF',
  'accent-primary-active': '#0958D9',
  'accent-stateless': colors.editorAccent,
  'link-pressed': colors.editorAccent,
  'bg-primary-active': '#E6F4FF',
  'bg-base-light': '#E6F4FF',
  'bg-base-medium': '#BAE0FF',
  'accent-0-8-overlay': 'rgba(22, 119, 255, 0.08)',
  'active-secondary-hover': 'rgba(22, 119, 255, 0.05)',
  'border-hover-bottom': 'rgba(22, 119, 255, 0.18)',
  'borders-disabled': 'rgba(22, 119, 255, 0.4)',
  'accent-stateless_0_4_opacity': 'rgba(22, 119, 255, 0.4)',
  accent_0_5_5_opacity: 'rgba(22, 119, 255, 0.55)',
  accent_0_5_opacity: 'rgba(22, 119, 255, 0.05)',
  accent_0_7_opacity: 'rgba(22, 119, 255, 0.7)',
  accent_1_2_opacity: 'rgba(22, 119, 255, 0.12)',
  accent_1_8_opacity: 'rgba(22, 119, 255, 0.18)',
  accent_2_8_opacity: 'rgba(22, 119, 255, 0.28)',
  accent_4_0_opacity: 'rgba(22, 119, 255, 0.4)',
};

// Filerobot ships English only; these cover the annotate-only chrome.
const EDITOR_TEXTS_DE = {
  save: 'Speichern',
  cancel: 'Abbrechen',
  apply: 'Übernehmen',
  confirm: 'Bestätigen',
  warning: 'Warnung',
  loading: 'Wird geladen …',
  annotateTabLabel: 'Markieren',
  arrowTool: 'Pfeil',
  ellipseTool: 'Ellipse',
  rectangleTool: 'Rechteck',
  polygonTool: 'Polygon',
  lineTool: 'Linie',
  penTool: 'Marker',
  textTool: 'Text',
  imageTool: 'Bild',
  undoTitle: 'Letzten Schritt rückgängig machen',
  redoTitle: 'Letzten Schritt wiederholen',
  resetOperations: 'Alle Änderungen zurücksetzen',
  showImageTitle: 'Originalbild anzeigen',
  zoomInTitle: 'Vergrößern',
  zoomOutTitle: 'Verkleinern',
  toggleZoomMenuTitle: 'Zoom-Menü umschalten',
  actualSize: 'Originalgröße (100%)',
  fitSize: 'Einpassen',
  discardChanges: 'Änderungen verwerfen',
  discardChangesWarningHint:
    'Wenn du schließt, geht die letzte Änderung verloren.',
  changesLoseWarningHint:
    'Beim Zurücksetzen gehen deine Änderungen verloren. Fortfahren?',
  stroke: 'Linienstärke',
  opacity: 'Deckkraft',
  transparency: 'Deckkraft',
  shadow: 'Schatten',
  size: 'Größe',
  fontFamily: 'Schriftart',
  textAlignment: 'Textausrichtung',
  textSpacings: 'Textabstände',
  letterSpacing: 'Zeichenabstand',
  lineHeight: 'Zeilenhöhe',
  cornerRadius: 'Eckenradius',
  sides: 'Seiten',
  invalidImageError: 'Ungültiges Bild.',
  mutualizedFailedToLoadImg: 'Bild konnte nicht geladen werden.',
};
const EDITOR_TEXTS_EN = { penTool: 'Highlighter', transparency: 'Opacity' };

const canvasFont = (a: any) => {
  const style = String(a.fontStyle || 'normal');
  const weight = style.includes('bold') ? 'bold' : 'normal';
  const slant = style.includes('italic') ? 'italic' : 'normal';
  return `${slant} ${weight} ${a.fontSize || 32}px ${a.fontFamily || 'Arial'}`;
};

// Filerobot sizes a text node from the drawn box and Konva clips whatever
// does not fit, so grow any box that is smaller than its own text. Only the
// two size keys are merged, so styling the user applied is left untouched.
const fitTextBoxes = (
  annotations: Record<string, any>,
  ctx: CanvasRenderingContext2D
) => {
  const grown: Record<string, { width: number; height: number }> = {};
  Object.values(annotations || {}).forEach((a) => {
    if (a?.name !== 'Text' || !a.text || !a.id) return;
    ctx.font = canvasFont(a);
    const width = Math.ceil(ctx.measureText(a.text).width) + 8;
    const height = Math.ceil((a.fontSize || 32) * (a.lineHeight || 1)) + 4;
    if ((a.width || 0) < width || (a.height || 0) < height) {
      grown[a.id] = {
        width: Math.max(a.width || 0, width),
        height: Math.max(a.height || 0, height),
      };
    }
  });
  return Object.keys(grown).length ? grown : null;
};

const PINNED_COLORS_KEY = 'FIE_pinnedColors';

const pinEditorColors = (colorsToPin: string[]) => {
  try {
    const current: string[] = JSON.parse(
      window.localStorage.getItem(PINNED_COLORS_KEY) || '[]'
    );
    const merged = [...colorsToPin, ...current].filter(
      (c, i, all) =>
        all.findIndex((x) => x.toLowerCase() === c.toLowerCase()) === i
    );
    window.localStorage.setItem(PINNED_COLORS_KEY, JSON.stringify(merged));
  } catch {
    // localStorage unavailable: the picker just shows no pins
  }
};

const ClaimImageAnnotateModal: React.FC = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const visible = useSelector(getIsVisibleModal(MODAL_NAME));
  const editingClaim = useSelector(selectEditingClaim);
  const brands = useSelector(selectBrands);
  const fleets = useSelector(selectFleets);
  const claimId: string | undefined = editingClaim?.claim?.id;
  // claim.brand is the vehicle make; the Getaway brand hangs off the fleet.
  const fleetBrandId = fleets?.find(
    (f) => f.id === editingClaim?.claim?.fleetId
  )?.brandId;
  const brand =
    brands?.find((b) => b.id === fleetBrandId) ||
    (brands?.length === 1 ? brands[0] : undefined);
  const markColor = asHexColor(brand?.primaryColor) || FALLBACK_MARK_COLOR;

  useEffect(() => {
    if (visible) pinEditorColors([markColor, FALLBACK_MARK_COLOR]);
  }, [visible, markColor]);
  const image = editingClaim?.image;
  const source = editingClaim?.source ?? 'claim-drawer';
  const [editorSource, setEditorSource] = useState<string | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [hasMarks, setHasMarks] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const getImgDataRef = useRef<any>(null);
  const updateStateRef = useRef<any>(null);
  const measureCtx = useRef<CanvasRenderingContext2D | null>(null);

  // The editor rasterises onto a canvas, so the photo must be CORS-readable;
  // an object URL from a fetched blob is the only way that works reliably
  // (a plain crossOrigin <img> hits Chrome's cached non-CORS thumbnail response).
  useEffect(() => {
    setLoadFailed(false);
    setHasMarks(false);
    setConfirmOpen(false);
    setSaving(false);
    if (!visible || !image?.url) {
      setEditorSource(null);
      return undefined;
    }
    let objectUrl: string | null = null;
    let cancelled = false;
    fetchImageBlob(image.url)
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob);
        if (!cancelled) setEditorSource(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [visible, image?.url]);

  const handleClose = () => {
    batch(() => {
      dispatch(closeModal(MODAL_NAME));
      dispatch(clearEditingClaim());
    });
  };

  // onModify fires from inside Filerobot's reducer, so the size fix is applied
  // on the next tick, as a function of the state current at that moment.
  const handleModify: FilerobotImageEditorConfig['onModify'] = (state) => {
    setHasMarks(Object.keys(state?.annotations || {}).length > 0);
    if (!measureCtx.current) {
      measureCtx.current = document.createElement('canvas').getContext('2d');
    }
    const ctx = measureCtx.current;
    if (!ctx || !updateStateRef.current) return;
    window.setTimeout(() => {
      updateStateRef.current?.((current: any) => {
        const grown = fitTextBoxes(current?.annotations, ctx);
        return grown ? { annotations: grown } : null;
      });
    }, 0);
  };

  const handleSave = async () => {
    if (!claimId || !getImgDataRef.current) return;
    setSaving(true);
    const result = getImgDataRef.current(SAVED_IMAGE, false, true);
    try {
      const saved = result?.imageData?.imageBase64
        ? await dispatch(
            submitAnnotatedClaimImage(
              claimId,
              result.imageData.imageBase64,
              image,
              source
            )
          )
        : false;
      if (saved) handleClose();
    } finally {
      result?.hideLoadingSpinner?.();
      setSaving(false);
      setConfirmOpen(false);
    }
  };

  const cancelLabel = intl.formatMessage({ id: 'cancel' });
  const defaultText = intl.formatMessage({
    id: 'fleetadmin.claim.image.annotate.textDefault',
    defaultMessage: 'Damage',
  });
  const saveLabel = intl.formatMessage({
    id: 'fleetadmin.claim.image.annotate.save',
    defaultMessage: 'Save edited photo',
  });

  return (
    <>
      <Modal
        wrapClassName="claim-image-annotate-modal"
        title={intl.formatMessage({
          id: 'fleetadmin.claim.image.annotate.title',
          defaultMessage: 'Highlight damage',
        })}
        open={visible}
        onCancel={handleClose}
        width="92vw"
        style={{ top: 16, paddingBottom: 0 }}
        zIndex={zIndex.annotateModal}
        destroyOnClose
        maskClosable={false}
        keyboard={false}
        // The editor sizes its canvas to the container; the zoom-in animation
        // makes it measure a tiny box first, so open without one.
        transitionName=""
        maskTransitionName=""
        footer={
          <div className="claim-image-annotate-footer">
            <Button
              data-testid="ClaimImageAnnotateModal-cancelBtn"
              className="cancel-button"
              onClick={handleClose}
            >
              {cancelLabel}
            </Button>
            <Button
              data-testid="ClaimImageAnnotateModal-saveBtn"
              className="submit-button"
              disabled={!editorSource || !hasMarks || saving}
              loading={saving}
              onClick={() => setConfirmOpen(true)}
            >
              {saveLabel}
            </Button>
          </div>
        }
      >
        <div
          className="claim-image-annotate-editor"
          data-testid="ClaimImageAnnotateModal-editor"
        >
          {loadFailed && (
            <Alert
              data-testid="ClaimImageAnnotateModal-loadError"
              type="error"
              showIcon
              message={intl.formatMessage({
                id: 'fleetadmin.claim.image.annotate.loadError',
                defaultMessage: 'The photo could not be loaded for editing',
              })}
              description={image?.url}
            />
          )}
          {visible && editorSource ? (
            <Suspense
              fallback={
                <div className="claim-image-annotate-loading">
                  <LoadingIcon />
                </div>
              }
            >
              <FilerobotImageEditor
                source={editorSource}
                tabsIds={['Annotate']}
                defaultTabId="Annotate"
                defaultToolId="Arrow"
                annotationsCommon={{
                  fill: 'transparent',
                  stroke: markColor,
                  strokeWidth: 6,
                  opacity: 1,
                }}
                Arrow={{ strokeWidth: 8 }}
                Pen={{ strokeWidth: 24, opacity: 0.45, lineCap: 'round' }}
                Text={
                  {
                    text: defaultText,
                    fill: markColor,
                    stroke: 'transparent',
                    strokeWidth: 0,
                    fontSize: 32,
                    fontStyle: 'bold',
                    // Forwarded to Konva: a drawn box narrower than the word
                    // would otherwise break it into stacked characters.
                    wrap: 'none',
                  } as FilerobotImageEditorConfig['Text']
                }
                savingPixelRatio={4}
                previewPixelRatio={window.devicePixelRatio || 1}
                language={intl.locale}
                translations={
                  intl.locale === 'de' ? EDITOR_TEXTS_DE : EDITOR_TEXTS_EN
                }
                useBackendTranslations={false}
                observePluginContainerSize
                removeSaveButton
                getCurrentImgDataFnRef={getImgDataRef}
                updateStateFnRef={updateStateRef}
                onModify={handleModify}
                onClose={handleClose}
                theme={{
                  palette: EDITOR_PALETTE,
                  typography: { fontFamily: 'Arial, Helvetica, sans-serif' },
                }}
              />
            </Suspense>
          ) : (
            !loadFailed && (
              <div className="claim-image-annotate-loading">
                <LoadingIcon />
              </div>
            )
          )}
        </div>
      </Modal>
      <Modal
        wrapClassName="claim-image-annotate-confirm"
        open={confirmOpen}
        onCancel={() => !saving && setConfirmOpen(false)}
        footer={null}
        closable={false}
        centered
        width={440}
        zIndex={zIndex.annotateModal + 10}
      >
        <div
          className="claim-image-annotate-confirm-body"
          data-testid="ClaimImageAnnotateModal-confirm"
        >
          <div className="claim-image-annotate-confirm-title">{saveLabel}</div>
          <p>
            {image?.hiddenFromUser
              ? intl.formatMessage({
                  id: 'fleetadmin.claim.image.annotate.confirmHidden',
                  defaultMessage:
                    'The edited photo will be added to the claim as a new image. The original is already hidden from the user.',
                })
              : intl.formatMessage({
                  id: 'fleetadmin.claim.image.annotate.confirm',
                  defaultMessage:
                    'The edited photo will be added to the claim as a new image and the original will be hidden from the user.',
                })}
          </p>
          <Button
            data-testid="ClaimImageAnnotateModal-confirmBtn"
            className="submit-button"
            loading={saving}
            onClick={handleSave}
          >
            {saveLabel}
          </Button>
          <Button
            className="cancel-button"
            disabled={saving}
            onClick={() => setConfirmOpen(false)}
          >
            {cancelLabel}
          </Button>
        </div>
      </Modal>
      <style jsx global>
        {styles}
      </style>
    </>
  );
};

export default ClaimImageAnnotateModal;
